import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Alert,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as OrderIcon,
  AttachMoney as RevenueIcon,
  Inventory as ProductIcon,
  LocalShipping as DeliveryIcon,
  People as CustomersIcon,
  Nature as OrganicIcon,
  Schedule as TimeIcon,
  Star as QualityIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Agriculture as FarmIcon,
  Recycling as SustainabilityIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  Legend
} from 'recharts';
import { PreOrderKPIs, ProductPerformance, CategoryPerformance } from '../../types/analytics';
import { analyticsService } from '../../services/analyticsService';

const COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#F44336'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const KPICard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: number;
  format?: 'number' | 'currency' | 'percentage' | 'days' | 'minutes';
  target?: number;
}> = ({ title, value, icon, color, subtitle, trend, format = 'number', target }) => {
  
  const formatValue = (val: string | number) => {
    const numVal = typeof val === 'string' ? parseFloat(val) : val;
    switch (format) {
      case 'currency':
        return `₹${numVal.toLocaleString()}`;
      case 'percentage':
        return `${numVal}%`;
      case 'days':
        return `${numVal} days`;
      case 'minutes':
        return `${numVal} min`;
      default:
        return numVal.toLocaleString();
    }
  };

  const getPerformanceColor = () => {
    if (!target) return color;
    const numVal = typeof value === 'string' ? parseFloat(value) : value;
    return numVal >= target ? '#4CAF50' : numVal >= target * 0.8 ? '#FF9800' : '#F44336';
  };

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: `${getPerformanceColor()}20`,
              color: getPerformanceColor(),
            }}
          >
            {icon}
          </Box>
          {trend !== undefined && (
            <Chip
              icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${trend > 0 ? '+' : ''}${trend}%`}
              size="small"
              color={trend > 0 ? 'success' : 'error'}
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          {formatValue(value)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
        {target && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min((typeof value === 'number' ? value : parseFloat(value as string)) / target * 100, 100)}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getPerformanceColor()
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Target: {formatValue(target)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const EnhancedDashboard: React.FC = () => {
  const [kpis, setKpis] = useState<PreOrderKPIs | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadKPIs = async () => {
    setLoading(true);
    setError(null);
    try {
      const kpiData = await analyticsService.getPreOrderKPIs();
      setKpis(kpiData);
    } catch (error) {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data on component mount
  useEffect(() => {
    loadKPIs();
  }, []);

  const organicVsConventionalData = kpis ? [
    { name: 'Organic', value: kpis.organicVsConventionalSplit.organic, color: '#4CAF50' },
    { name: 'Conventional', value: kpis.organicVsConventionalSplit.conventional, color: '#FF9800' }
  ] : [];

  const categoryRevenueData = kpis ? kpis.categoryPerformance.map(cat => ({
    name: cat.category,
    revenue: cat.revenue,
    orders: cat.totalOrders,
    growth: cat.growthRate
  })) : [];

  const seasonalTrendData = kpis ? kpis.seasonalDemandTrends.map(trend => ({
    month: trend.month,
    orders: trend.orders,
    revenue: trend.revenue / 1000, // Convert to thousands
    demandFactor: trend.demandFactor * 100
  })) : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Real-time KPIs and insights from your Shetkari business data
          </Typography>
        </Box>
        <IconButton
          onClick={loadKPIs}
          disabled={loading}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': { bgcolor: 'primary.dark' },
            '&:disabled': { bgcolor: 'action.disabledBackground' }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3 }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {!kpis && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Click the refresh button to load dashboard data.
        </Alert>
      )}

      {/* Tabs for different KPI categories */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Business Overview" />
          <Tab label="Pre-Order Performance" />
          <Tab label="Product Analytics" />
          <Tab label="Supply Chain" />
          <Tab label="Customer Insights" />
        </Tabs>
      </Box>

      {/* Business Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        {kpis && (
          <>
            {/* Core Business KPIs */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Total Pre-Orders"
                  value={kpis.totalPreOrders}
                  icon={<OrderIcon />}
                  color="#2196F3"
                  target={2000}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Total Revenue"
                  value={kpis.totalRevenue}
                  icon={<RevenueIcon />}
                  color="#4CAF50"
                  format="currency"
                  target={300000}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Average Order Value"
                  value={kpis.averageOrderValue}
                  icon={<AnalyticsIcon />}
                  color="#FF9800"
                  format="currency"
                  target={180}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Customer Retention"
                  value={kpis.customerRetentionRate}
                  icon={<CustomersIcon />}
                  color="#9C27B0"
                  format="percentage"
                  target={80}
                />
              </Box>
            </Box>

            {/* Financial Health */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Gross Margin"
                  value={kpis.grossMargin}
                  icon={<RevenueIcon />}
                  color="#4CAF50"
                  format="percentage"
                  target={40}
                  subtitle="Requires cost tracking"
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Operating Margin"
                  value={kpis.operatingMargin}
                  icon={<AnalyticsIcon />}
                  color="#2196F3"
                  format="percentage"
                  target={25}
                  subtitle="Requires cost tracking"
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Cash Flow from Pre-Orders"
                  value={kpis.cashFlowFromPreOrders}
                  icon={<RevenueIcon />}
                  color="#FF9800"
                  format="currency"
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Inventory Turnover"
                  value={kpis.inventoryTurnover}
                  icon={<ProductIcon />}
                  color="#9C27B0"
                  format="number"
                  target={8}
                  subtitle="Requires inventory tracking"
                />
              </Box>
            </Box>

            {/* Charts Row */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '2 1 600px', minWidth: '600px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      Revenue & Order Trends
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={seasonalTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#666" />
                        <YAxis stroke="#666" />
                        <RechartsTooltip />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke="#4CAF50"
                          fill="#4CAF5020"
                          strokeWidth={3}
                        />
                        <Area
                          type="monotone"
                          dataKey="orders"
                          stroke="#2196F3"
                          fill="#2196F320"
                          strokeWidth={3}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      Organic vs Conventional
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={organicVsConventionalData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {organicVsConventionalData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </>
        )}
      </TabPanel>

      {/* Pre-Order Performance Tab */}
      <TabPanel value={tabValue} index={1}>
        {kpis && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Conversion Rate"
                  value={kpis.preOrderConversionRate}
                  icon={<TrendingUpIcon />}
                  color="#4CAF50"
                  format="percentage"
                  subtitle="Inquiries to orders"
                  target={75}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Fulfillment Rate"
                  value={kpis.preOrderFulfillmentRate}
                  icon={<SuccessIcon />}
                  color="#2196F3"
                  format="percentage"
                  subtitle="Successfully delivered"
                  target={95}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Average Lead Time"
                  value={kpis.averagePreOrderLeadTime}
                  icon={<TimeIcon />}
                  color="#FF9800"
                  format="days"
                  subtitle="Order to delivery"
                  target={2}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Cancellation Rate"
                  value={kpis.preOrderCancellationRate}
                  icon={<WarningIcon />}
                  color="#F44336"
                  format="percentage"
                  subtitle="Pre-orders cancelled"
                  target={5}
                />
              </Box>
            </Box>

            {/* Operational Efficiency */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Order Processing Time"
                  value={kpis.orderProcessingTime}
                  icon={<TimeIcon />}
                  color="#9C27B0"
                  format="minutes"
                  target={10}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Delivery Efficiency"
                  value={kpis.deliveryEfficiency}
                  icon={<DeliveryIcon />}
                  color="#00BCD4"
                  format="percentage"
                  target={95}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Waste Percentage"
                  value={kpis.wastePercentage}
                  icon={<WarningIcon />}
                  color="#F44336"
                  format="percentage"
                  subtitle="Product waste"
                  target={3}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Packaging Cost"
                  value={kpis.packagingCostPerOrder}
                  icon={<ProductIcon />}
                  color="#795548"
                  format="currency"
                  subtitle="Per order"
                  target={10}
                />
              </Box>
            </Box>
          </>
        )}
      </TabPanel>

      {/* Product Analytics Tab */}
      <TabPanel value={tabValue} index={2}>
        {kpis && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            <Box sx={{ flex: '2 1 600px', minWidth: '600px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Category Performance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryRevenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="revenue" fill="#4CAF50" />
                      <Bar dataKey="orders" fill="#2196F3" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Top Performing Products
                  </Typography>
                  {kpis.topSellingProducts.map((product, index) => (
                    <Box key={product.productId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {product.productName}
                        </Typography>
                        <Chip
                          size="small"
                          label={product.demandTrend}
                          color={product.demandTrend === 'increasing' ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Orders: {product.totalOrders} | Revenue: ₹{product.revenue.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <QualityIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                        <Typography variant="caption">
                          {product.averageRating} ({product.customerSatisfaction}% satisfaction)
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Supply Chain Tab */}
      <TabPanel value={tabValue} index={3}>
        {kpis && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Supplier Reliability"
                  value={kpis.supplierReliabilityScore}
                  icon={<FarmIcon />}
                  color="#4CAF50"
                  format="percentage"
                  target={90}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Freshness Satisfaction"
                  value={kpis.freshnessSatisfactionScore}
                  icon={<QualityIcon />}
                  color="#2196F3"
                  format="percentage"
                  target={90}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="On-Time Delivery"
                  value={kpis.deliveryOnTimeRate}
                  icon={<DeliveryIcon />}
                  color="#FF9800"
                  format="percentage"
                  target={95}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <KPICard
                  title="Quality Complaints"
                  value={kpis.qualityComplaintRate}
                  icon={<WarningIcon />}
                  color="#F44336"
                  format="percentage"
                  subtitle="Of total orders"
                  target={2}
                />
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Supply Chain Insights:</strong> Focus on improving supplier reliability and reducing quality complaints to enhance customer satisfaction and reduce operational costs.
              </Typography>
            </Alert>
          </>
        )}
      </TabPanel>

      {/* Customer Insights Tab */}
      <TabPanel value={tabValue} index={4}>
        {kpis && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <KPICard
                title="Customer Acquisition Cost"
                value={kpis.customerAcquisitionCost}
                icon={<CustomersIcon />}
                color="#9C27B0"
                format="currency"
                target={40}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <KPICard
                title="Customer Lifetime Value"
                value={kpis.customerLifetimeValue}
                icon={<TrendingUpIcon />}
                color="#4CAF50"
                format="currency"
                target={1500}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <KPICard
                title="Repeat Customer Rate"
                value={kpis.repeatCustomerRate}
                icon={<CustomersIcon />}
                color="#2196F3"
                format="percentage"
                target={75}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <KPICard
                title="Order Frequency"
                value={kpis.averageCustomerOrderFrequency}
                icon={<OrderIcon />}
                color="#FF9800"
                subtitle="Orders per month"
                target={3}
              />
            </Box>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default EnhancedDashboard;
